import React, { useEffect, useMemo, useState } from "react";
import {
  FiCornerUpLeft,
  FiEdit2,
  FiEye,
  FiPlus,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import api, { apiError } from "../../lib/api";
import RenderToast from "../../appcomponents/RenderToast";
import TableComp from "../../appcomponents/TableComp";
import PaginationComp from "../../appcomponents/PaginationComp";
import AddEditBlogModal from "../../Modal/Blog/AddEditBlogModal";
import ViewBlogModal from "../../Modal/Blog/ViewBlogModal";
import DeleteBlogModal from "../../Modal/Blog/DeleteBlogModal";
import useDebounce from "../../hooks/useDebounce";
import { hasPermission, selectUser } from "../../store/authSlice";
import styles from "./Blogs.module.css";

const TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "trash", label: "Trash" },
];

const statusOptions = [
  { key: "all", label: "All Status" },
  { key: "publish", label: "Publish" },
  { key: "schedule", label: "Schedule" },
  { key: "draft", label: "Draft" },
];

const statusBadgeClass = (statusSlug, isTrashed) => {
  if (isTrashed) return styles.trash;
  if (statusSlug === "publish") return styles.publish;
  if (statusSlug === "schedule") return styles.schedule;
  return styles.draft;
};

export default function Blogs() {
  const currentUser = useSelector(selectUser);
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [viewBlog, setViewBlog] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [recoverLoadingId, setRecoverLoadingId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("soft");
  const debouncedSearch = useDebounce(search, 350);
  const canAdd = hasPermission(currentUser, "blogs", "add");
  const canView = hasPermission(currentUser, "blogs", "view");
  const canEdit = hasPermission(currentUser, "blogs", "edit");
  const canDelete = hasPermission(currentUser, "blogs", "delete");
  const canDeleteAll = hasPermission(currentUser, "blogs", "delete_all");

  const currentTab = useMemo(() => {
    if (activeTab === "trash") return "trash";
    if (activeTab === "draft") return "draft";
    return "all";
  }, [activeTab]);

  const fetchData = async (tab = currentTab, pageNumber = page) => {
    setLoading(true);
    try {
      const blogsRequest = api.get("/api/blogs.php", {
        params: {
          tab,
          search: debouncedSearch,
          page: pageNumber,
          per_page: 10,
          status: statusFilter,
        },
      });

      const needsEditorData = canAdd || canEdit;
      const editorRequests = needsEditorData
        ? Promise.allSettled([
            api.get("/api/statuses.php"),
            api.get("/api/categories.php", { params: { per_page: 200 } }),
            api.get("/api/tags.php", { params: { per_page: 200 } }),
          ])
        : Promise.resolve([]);

      const [blogsRes, editorRes] = await Promise.all([
        blogsRequest,
        editorRequests,
      ]);

      const blogPayload = blogsRes.data?.data || {};
      setBlogs(blogPayload.items || []);
      setTotal(blogPayload.total || 0);
      if (needsEditorData && Array.isArray(editorRes)) {
        setStatuses(
          editorRes[0]?.status === "fulfilled"
            ? editorRes[0].value.data?.data?.items || []
            : [],
        );
        setCategories(
          editorRes[1]?.status === "fulfilled"
            ? editorRes[1].value.data?.data?.items || []
            : [],
        );
        setTags(
          editorRes[2]?.status === "fulfilled"
            ? editorRes[2].value.data?.data?.items || []
            : [],
        );
      } else {
        setStatuses([]);
        setCategories([]);
        setTags([]);
      }
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, debouncedSearch, page, statusFilter]);

  const visibleSelectionCount = selectedIds.filter((id) =>
    blogs.some((blog) => Number(blog.id) === Number(id)),
  ).length;

  const allSelected =
    blogs.length > 0 && visibleSelectionCount === blogs.length;

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : blogs.map((blog) => blog.id));
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const openCreate = () => {
    if (!canAdd) return;
    setCurrentBlog(null);
    setModalOpen(true);
  };

  const openEdit = (blog) => {
    setCurrentBlog(blog);
    setModalOpen(true);
  };

  const openView = (blog) => setViewBlog(blog);

  const promptDelete = (mode = "soft", blogIds = selectedIds) => {
    if (!blogIds.length) {
      RenderToast({ type: "error", message: "Select at least one blog." });
      return;
    }
    setSelectedIds(blogIds);
    setDeleteMode(mode);
    setConfirmDelete(true);
  };

  const confirmRemoval = async () => {
    setDeleteLoading(true);
    try {
      const ids = selectedIds.length
        ? selectedIds
        : currentBlog
          ? [currentBlog.id]
          : [];
      const payload = {
        action: ids.length > 1 ? "bulk_delete" : "delete",
        ids,
        id: ids[0],
        permanent: deleteMode === "hard",
      };

      const response = await api.post("/api/blogs.php", payload);
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to delete blog.");
      }
      RenderToast({
        type: "success",
        message:
          ids.length > 1
            ? `${ids.length} blog${ids.length > 1 ? "s" : ""} deleted successfully.`
            : "Blog deleted successfully.",
      });
      setConfirmDelete(false);
      setCurrentBlog(null);
      clearSelection();
      fetchData();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRecover = async (blog) => {
    setRecoverLoadingId(blog.id);
    try {
      const attemptPayloads = [
        { action: "restore", id: blog.id, status_id: "2" },
        {
          action: "update",
          id: blog.id,
          status_id: "2",
          is_trashed: 0,
          restore: true,
        },
      ];

      let success = false;
      let lastError = null;

      for (const payload of attemptPayloads) {
        try {
          const response = await api.post("/api/blogs.php", payload);
          if (response.data?.success) {
            success = true;
            break;
          }
          lastError = response.data?.message || "Unable to recover blog.";
        } catch (err) {
          lastError = apiError(err);
        }
      }

      if (!success) {
        throw new Error(lastError || "Unable to recover blog.");
      }

      RenderToast({
        type: "success",
        message: "Blog recovered to draft successfully.",
      });
      setActiveTab("draft");
      setPage(1);
      await fetchData("draft", 1);
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setRecoverLoadingId(null);
    }
  };

  const columns = [
    {
      key: "check",
      label: (
        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() => toggleSelected(row.id)}
        />
      ),
    },
    {
      key: "image",
      label: "Image",
      render: (row) =>
        row.image_url ? (
          <img
            className={styles.blogThumb}
            src={row.image_url}
            alt={row.title}
          />
        ) : (
          <span className={styles.emptyThumb}>-</span>
        ),
    },
    {
      key: "title",
      label: "Blog Title",
      render: (row) => {
        const shortTitle = row.title?.split(" ").slice(0, 5).join(" ") || "";

        return (
          <span title={row.title}>
            {shortTitle}
            {row.title?.split(" ").length > 5 ? "..." : ""}
          </span>
        );
      },
    },

    {
      key: "excerpt",
      label: "Blog Description",
      // render: (row) => <span className={styles.excerpt}>{row.excerpt}</span>,
      render: (row) => {
        const shortExcerpt =
          row.excerpt?.split(/\s+/).slice(0, 5).join(" ") || "";

        return (
          <span title={row.excerpt}>
            {shortExcerpt}
            {row.excerpt?.split(/\s+/).length > 5 ? "..." : ""}
          </span>
        );
      },
    },
    {
      key: "author",
      label: "Author",
      render: (row) => row.author || row.created_by_name || "-",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={`${styles.statusBadge} ${statusBadgeClass(row.status_slug, row.is_trashed)}`}
        >
          {row.is_trashed ? "Trash" : row.status_name || "Draft"}
        </span>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <div className={styles.actions}>
          {row.is_trashed ? (
            <>
              <button
                type="button"
                onClick={() => handleRecover(row)}
                title="Recover to Draft"
                disabled={recoverLoadingId === row.id}
              >
                <FiCornerUpLeft />
              </button>
              {canDeleteAll ? (
                <button
                  type="button"
                  onClick={() => {
                    setCurrentBlog(row);
                    promptDelete("hard", [row.id]);
                  }}
                  title="Delete Permanently"
                >
                  <FiTrash2 />
                </button>
              ) : null}
            </>
          ) : null}
          {!row.is_trashed ? (
            <>
              {canView ? (
                <button type="button" onClick={() => openView(row)} title="View">
                  <FiEye />
                </button>
              ) : null}
              {canEdit ? (
                <button type="button" onClick={() => openEdit(row)} title="Edit">
                  <FiEdit2 />
                </button>
              ) : null}
              {canDelete ? (
                <button
                  type="button"
                  onClick={() => {
                    setCurrentBlog(row);
                    promptDelete("soft", [row.id]);
                  }}
                  title="Delete"
                >
                  <FiTrash2 />
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      ),
    },
  ];

  const selectionCount = selectedIds.length;
  const selectionLabel =
    selectionCount > 1
      ? `Delete ${selectionCount} Blogs`
      : selectionCount === 1
        ? "Delete 1 Blog"
        : "Delete";
  const showDeleteSelection =
    selectionCount > 0 &&
    (activeTab === "trash" ? canDeleteAll : canDelete);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1>Blogs</h1>
          <p>
            Manage publish, draft, schedule and trash posts from one screen.
          </p>
        </div>
      </div>

      <div className={styles.controlsTop}>
        <div className={styles.searchBox}>
          <FiSearch />
          <input
            type="search"
            placeholder="Search blogs..."
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>

        <div className={styles.rightActions}>
          {showDeleteSelection && (
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() =>
                promptDelete(activeTab === "trash" ? "hard" : "soft")
              }
            >
              <FiTrash2 />
              {selectionLabel}
            </button>
          )}
          {canAdd ? (
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={openCreate}
            >
              <FiPlus />
              Add Blog
            </button>
          ) : null}
          <select
            className={styles.filter}
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value);
            }}
          >
            {statusOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${styles.tabBtn} ${activeTab === tab.key ? styles.active : ""}`}
            onClick={() => {
              setPage(1);
              setActiveTab(tab.key);
              setSelectedIds([]);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <TableComp
        columns={columns}
        rows={blogs}
        loading={loading}
        emptyMessage="No blogs found."
      />

      <div className={styles.paginationRow}>
        <PaginationComp
          total={total}
          perPage={10}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>

      <AddEditBlogModal
        open={modalOpen}
        blog={currentBlog}
        statuses={statuses}
        categories={categories}
        tags={tags}
        canEditSchema={canAdd || canEdit}
        onClose={() => setModalOpen(false)}
        onSaved={fetchData}
      />

      <ViewBlogModal blog={viewBlog} onClose={() => setViewBlog(null)} />

      <DeleteBlogModal
        open={confirmDelete}
        loading={deleteLoading}
        title={selectedIds.length > 1 ? "Delete selected blogs" : "Delete blog"}
        description={
          deleteMode === "hard"
            ? "This will permanently delete the selected blog(s) and all related files."
            : "This will move the selected blog(s) to trash."
        }
        onCancel={() => setConfirmDelete(false)}
        onConfirm={confirmRemoval}
      />
    </div>
  );
}
